// Database types - using snake_case to match Supabase
export interface DbTask {
  id: string;
  title: string;
  description: string | null;
  due_date: string | null;
  priority: string | null;
  is_completed: boolean;
  created_at: string;
  updated_at: string;
  user_id: string;
  card_order: number | null; // ✅ FIXED: Made nullable to match actual table schema
  goal_id?: string | null; // new for goal linkage!
  suggested_task_context_sent: boolean; // tracks if task context has been sent to AI
  suggested_research_context_sent: boolean; // tracks if task research context has been sent to AI
  // New generated content tracking fields
  generated_document_id: string | null;
  generated_image_id: string | null;
  generated_research_id: string | null;
  planner_tasks_assignees?: Array<{
    assignee_id: string;
    assigned_at?: string;
    assigned_by?: string;
    public_profile?: {
      id: string;
      first_name: string | null;
      username: string | null;
      profile_picture_url: string | null;
    } | null;
  }>; // Enhanced: Assignment data with full profile information via foreign key
}

// Types for insert operations
export interface TaskInsert {
  title: string;
  description?: string | null;
  due_date?: string | null;
  priority?: string | null;
  is_completed?: boolean;
  user_id: string;
  card_order?: number | null; // ✅ FIXED: Made nullable to match table schema
  goal_id?: string | null;
  suggested_task_context_sent?: boolean;
  suggested_research_context_sent?: boolean;
}

// Types for update operations
export interface TaskUpdate {
  title?: string;
  description?: string | null;
  due_date?: string | null;
  priority?: string | null;
  is_completed?: boolean;
  card_order?: number | null; // ✅ FIXED: Made nullable to match table schema
  goal_id?: string | null;
  suggested_task_context_sent?: boolean;
  suggested_research_context_sent?: boolean;
  // New generated content tracking fields
  generated_document_id?: string | null;
  generated_image_id?: string | null;
  generated_research_id?: string | null;
}

// Mappers between DB and UI types
export function mapDbTaskToUiTask(dbTask: DbTask): import('@/types/tasks').Task {
  // Map assignee data from database structure
  const assignees = dbTask.planner_tasks_assignees && Array.isArray(dbTask.planner_tasks_assignees)
    ? dbTask.planner_tasks_assignees
        .filter(assignment => assignment && assignment.assignee_id && assignment.public_profile) // Filter out null assignments
        .map(assignment => ({
          assignment_id: '', // Not available in this structure, but not needed for filtering
          assignee_id: assignment.assignee_id,
          first_name: assignment.public_profile?.first_name || '',
          username: assignment.public_profile?.username || '',
          profile_picture_url: assignment.public_profile?.profile_picture_url || undefined,
          display_name: assignment.public_profile?.first_name || assignment.public_profile?.username || 'Unknown User',
          assigned_at: assignment.assigned_at || '',
          assigned_by_name: 'Unknown' // Not available in this structure
        }))
    : [];

  return {
    id: dbTask.id,
    title: dbTask.title,
    description: dbTask.description || undefined,
    dueDate: dbTask.due_date ? dbTask.due_date.split('T')[0] : undefined, // Convert ISO datetime to YYYY-MM-DD
    priority: dbTask.priority as any || null,
    isCompleted: dbTask.is_completed,
    createdAt: dbTask.created_at,
    cardOrder: dbTask.card_order || 0, // ✅ FIXED: Handle nullable card_order with default value
    goalId: dbTask.goal_id || undefined,
    assignees: assignees, // ✅ NEW: Include assignee data for filtering
    // New generated content tracking fields
    generated_document_id: dbTask.generated_document_id,
    generated_image_id: dbTask.generated_image_id,
    generated_research_id: dbTask.generated_research_id,
  };
}

export function mapUiTaskToDbTask(uiTask: import('@/types/tasks').Task): TaskUpdate {
  return {
    title: uiTask.title,
    description: uiTask.description || null,
    due_date: uiTask.dueDate || null,
    priority: uiTask.priority || null,
    is_completed: uiTask.isCompleted || false,
    card_order: uiTask.cardOrder,
    goal_id: uiTask.goalId || null,
  };
}
